import { config } from "@config/config";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { FeatureFlagGate } from "@utils/FeatureFlags/FeatureFlagGate";
import { unwrapConnection } from "@utils/utils";
import ApexCharts from "apexcharts";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { Headline } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportTargetAiProgressSummaryPredictionStatusChoices,
  CatalightGoalStatus,
  ReportGoalType,
  ReportTargetType,
  useReportTargetPanelSingleViewQuery,
  useStartTargetProgressSummaryWorkflowMutation,
  useUpdateTargetProgressSummaryMutation,
} from "@api/graphql/types-and-hooks";
import { InlineEditField } from "@components/ProgrammingReview/InlineEditField";
import { InlineEditFieldWithAI } from "@components/ProgrammingReview/InlineEditFieldWithAI";
import { CATALIGHT_GOAL_STATUS_SELECT_OPTIONS } from "@components/ShortTermGoalPanel/catalightConstants";
import { getTargetFieldGenerationStatus } from "@components/ShortTermGoalPanel/helpers";
import { TargetProgressGraph } from "@components/TargetPanel/TargetProgressGraph";
import { TreatmentPlanForm } from "@components/TreatmentPlanFieldForm/TreatmentPlanForm";
import { useInlineTreatmentPlanEdit } from "@components/TreatmentPlanFieldForm/useInlineTreatmentPlanEdit";
import { useTreatmentPlanTemplate } from "@components/TreatmentPlanFieldForm/useTreatmentPlanTemplate";
import { targetToFieldValues } from "@components/TreatmentPlanFieldForm/utils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { ApprovalActionBar } from "@pages/ClinicalReportDetails/ReportPage/Components/ApprovalActionBar";
import { ApprovalBanner } from "@pages/ClinicalReportDetails/ReportPage/Components/ApprovalBanner";
import { GoalBadge } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalBadge";
import { InlineEditorSkeleton } from "@pages/ClinicalReportDetails/ReportPage/Components/InlineEditorSkeleton";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { useReport } from "@providers/ReportProvider";

interface TargetInlineEditorNavigation {
  onPreviousGoal?: () => void;
  onNextGoal?: () => void;
  onPreviousTarget?: () => void;
  onNextTarget?: () => void;
  /**
   * Called after a successful Approve to walk forward to the next UNAPPROVED
   * item in the goal/target stack (skipping anything already approved).
   * Receives the id of the item that was just approved so the handler can
   * detect a manual navigation during the toast window and skip the advance.
   */
  onAdvanceAfterApprove?: (fromItemId: string) => void;
}

interface TargetInlineEditorProps {
  target: ReportTargetType;
  goal: ReportGoalType;
  navigation?: TargetInlineEditorNavigation;
}

export const TargetInlineEditor: React.FC<TargetInlineEditorProps> = ({ target, goal, navigation }) => {
  const { isReportCompleted, isAddTargetsEnabled } = useClinicalReportDetailsData();
  const { report } = useReport();
  // See GoalInlineEditor — toast.onClose captures a stale closure; reading
  // navigation through a ref lets a manual sidebar navigation during the
  // toast window short-circuit the auto-advance.
  const navigationRef = useRef(navigation);
  navigationRef.current = navigation;
  const queryClient = useQueryClient();
  const learnerId = report.learner?.id || null;

  const chartRef = useRef<ApexCharts>(null);

  const updateTargetProgressSummaryMutation = useUpdateTargetProgressSummaryMutation({});

  const [currentTargetId, setCurrentTargetId] = useState<string>(target.id);
  const [localProgressSummary, setLocalProgressSummary] = useState<string>("");

  useEffect(() => {
    if (goal.id.startsWith("draft-") || target.id.startsWith("draft-")) {
      return;
    }
    setCurrentTargetId(target.id);
  }, [target.id, goal.id]);

  const targetPanelQuery = useReportTargetPanelSingleViewQuery(
    {
      filter: {
        learnerId: learnerId || "",
        targetId: currentTargetId,
        goalId: goal.id,
      },
    },
    {
      queryKey: ["getReportTargets", { reportGoalId: goal.id }, currentTargetId],
      enabled: !!goal.id && !!currentTargetId && !goal.id.startsWith("draft-") && !currentTargetId.startsWith("draft-"),
      refetchOnWindowFocus: false,
      refetchInterval: (query) => {
        const node = query.state.data?.getReportTargets?.edges?.[0]?.node;
        if (!node) return false;
        const isPending =
          node.aiProgressSummaryPredictionStatus ===
          ApiReportTargetAiProgressSummaryPredictionStatusChoices.GoalPredictionPending;
        return isPending ? config.REPORT_POLLING_INTERVAL : false;
      },
    },
  );

  const { targetFields } = useTreatmentPlanTemplate(report.id);

  const targetData = targetPanelQuery.data?.getReportTargets?.edges?.[0]?.node || undefined;

  const invalidateTargetQuery = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["getReportTargets", { reportGoalId: goal.id }, currentTargetId],
    });
    queryClient.invalidateQueries({
      queryKey: ["get-reassessment-goals", report.id],
    });
  }, [queryClient, goal.id, currentTargetId]);

  useEffect(() => {
    if (targetData) {
      setLocalProgressSummary(targetData.progressSummary ?? "");
    }
  }, [targetData]);

  const handleUpdateTargetProgressSummary = async (
    targetId: string,
    isApproved: boolean,
    progressSummary: string | undefined,
  ) => {
    let chartImageUri = "";
    if (isApproved) {
      const chartInstance = chartRef.current;
      if (chartInstance && !!chartInstance.getSeriesTotal().length) {
        try {
          const imageBlob = (await chartInstance.dataURI()) as { imgURI: string };
          chartImageUri = imageBlob.imgURI || "";
        } catch (error) {
          console.error("Failed to export target chart:", error);
        }
      }
    }

    await updateTargetProgressSummaryMutation.mutateAsync(
      {
        input: {
          targetId,
          isApproved,
          progressSummary,
          chartImageUri,
        },
      },
      {
        onSuccess: async (response) => {
          if (!response.updateTargetProgressSummary?.status) {
            notifyError("Failed to update target progress summary.");
            return;
          }
          invalidateTargetQuery();
          // Auto-advance only on approve (not on revoke). Capture the
          // approved target id and route through the navigation ref so a
          // manual navigation during the toast window can short-circuit
          // the advance in ProgrammingPage.
          if (isApproved) {
            notifySuccess("Target approved.", {
              onClose: () => navigationRef.current?.onAdvanceAfterApprove?.(targetId),
            });
          }
        },
        onError: async () => {
          notifyError("Failed to update target progress summary.");
        },
      },
    );
  };

  const { saveTargetFields, saveTargetCustomField } = useInlineTreatmentPlanEdit({
    goalId: goal.id,
    reportId: report.id,
    domainId: goal.reportDomain?.id || "",
    learnerId: learnerId || "",
    targetId: currentTargetId,
    onSuccess: () => {
      invalidateTargetQuery();
    },
  });

  const saveTargetProgressSummary = useCallback(
    async (payload: Record<string, unknown>): Promise<{ success: boolean; error?: string }> => {
      if ("progressSummary" in payload) {
        setLocalProgressSummary((payload.progressSummary as string) || "");
      }
      return saveTargetFields(payload);
    },
    [saveTargetFields],
  );

  const startTargetProgressSummaryWorkflowMutation = useStartTargetProgressSummaryWorkflowMutation();
  const handleRegenerateSummary = () => {
    startTargetProgressSummaryWorkflowMutation.mutateAsync(
      {
        reportId: report.id,
        targetId: currentTargetId,
      },
      {
        onSuccess(response) {
          if (response.startTargetProgressSummaryWorkflow?.status) {
            invalidateTargetQuery();
          } else {
            notifyError("Failed to regenerate target progress summary.");
          }
        },
        onError() {
          notifyError("Failed to regenerate target progress summary.");
        },
      },
    );
  };

  const { progressSummaryGenerating, progressSummaryFailed } = getTargetFieldGenerationStatus(
    targetData?.aiProgressSummaryPredictionStatus || null,
  );

  const isTargetApproved = targetData?.summaryReviewed || false;
  const isFieldReadOnly = isReportCompleted || isTargetApproved;

  if (targetPanelQuery.isLoading) {
    return <InlineEditorSkeleton />;
  }

  if (!targetData) {
    return (
      <div className="flex items-center justify-center py-16">
        <span className="text-lg text-limestone-400">Target data not found.</span>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {isTargetApproved && (
        <ApprovalBanner
          approvedAt={targetData?.summaryReviewedOn}
          onRevoke={() => handleUpdateTargetProgressSummary(targetData.id, false, localProgressSummary || undefined)}
        />
      )}
      <div className="flex-1 overflow-y-auto">
        <div key={targetData?.id ?? "empty"} className="mx-auto max-w-4xl py-8 animate-editor-content-in">
          <div className="flex min-w-0 flex-1 flex-col rounded-2xl border border-limestone-100 bg-white">
            {/* Section 1: Header with target details */}
            <div className="flex flex-col gap-2 border-b border-limestone-100 px-8 py-6">
              {/* Breadcrumb: Goal Name > Target Name + Status Badge */}
              <div className="flex items-center gap-8">
                <div className="flex min-w-0 flex-1 items-center">
                  <div className="flex min-w-0 flex-1 items-center gap-1 py-2">
                    <Headline
                      size="medium"
                      className="max-w-[130px] shrink-0 truncate font-semibold text-limestone-300 ph-no-capture"
                    >
                      {goal.goalName || "Goal"}
                    </Headline>
                    <ChevronRightIcon className="h-4 w-4 shrink-0 text-limestone-400" />
                    <div className="min-w-0 flex-1 ph-no-capture">
                      <InlineEditField
                        value={targetData?.targetName || ""}
                        fieldKey="targetName"
                        onSave={saveTargetFields}
                        title=""
                        required
                        isReportCompleted={isFieldReadOnly}
                        valueVariant="headline"
                      />
                    </div>
                  </div>
                </div>
                <GoalBadge
                  status={targetData.status}
                  expanded
                  onStatusChange={(id) => {
                    saveTargetFields({ status: id as CatalightGoalStatus });
                  }}
                  statusOptions={CATALIGHT_GOAL_STATUS_SELECT_OPTIONS}
                  isReadOnly={isFieldReadOnly}
                />
              </div>

              <TreatmentPlanForm
                templateFields={targetFields}
                nativeFieldValues={targetToFieldValues(targetData)}
                onNativeFieldSave={saveTargetFields}
                customFields={unwrapConnection(targetData.customFields) ?? []}
                onCustomFieldSave={saveTargetCustomField}
                onImageUploaded={invalidateTargetQuery}
                domain={goal.reportDomain ?? null}
                fieldOverrides={{
                  target_name: null,
                  status: null,
                  progress_summary: (
                    <InlineEditFieldWithAI
                      value={localProgressSummary}
                      fieldKey="progressSummary"
                      onSave={saveTargetProgressSummary}
                      title="Progress Summary"
                      isMultiline
                      isReportCompleted={isFieldReadOnly}
                      isGenerating={progressSummaryGenerating}
                      isGenerated={!!targetData.progressSummary}
                      isError={progressSummaryFailed}
                      errorMessage="AI progress summary generation failed"
                      onRegenerate={handleRegenerateSummary}
                      subTitle="How this target has progressed over the last authorization period"
                      canRegenerate={!isTargetApproved && !isReportCompleted}
                    />
                  ),
                }}
                isReportCompleted={isFieldReadOnly}
              />
            </div>

            {/* Section 3: Graph */}
            <FeatureFlagGate flagKey="reassessment-builder">
              {targetData ? (
                <div className="flex flex-col items-start pt-8">
                  <div className="w-full pb-8">
                    <TargetProgressGraph
                      reportGoalId={goal.id}
                      target={targetData}
                      signedChartImageUrl={targetData?.signedChartImageUrl || ""}
                      chartRef={chartRef}
                      loaderClassName="w-[55vw] mx-auto"
                    />
                  </div>
                </div>
              ) : null}
            </FeatureFlagGate>
          </div>
        </div>
        {/* Floating Action Bar */}
        {targetData && !isReportCompleted && (
          <ApprovalActionBar
            isApproved={isTargetApproved}
            isPending={updateTargetProgressSummaryMutation.isPending}
            onApprove={() => handleUpdateTargetProgressSummary(targetData.id, true, localProgressSummary || undefined)}
            onRevoke={() => handleUpdateTargetProgressSummary(targetData.id, false, localProgressSummary || undefined)}
            onPreviousGoal={navigation?.onPreviousGoal}
            onNextGoal={navigation?.onNextGoal}
            onPreviousTarget={navigation?.onPreviousTarget}
            onNextTarget={navigation?.onNextTarget}
            showTargetNavigation={isAddTargetsEnabled}
          />
        )}
      </div>
    </div>
  );
};
